import { Component, OnInit } from '@angular/core';
import { NavigationStart } from '@angular/router';
import { IconComponent, IWindowResizeEvent, windowEventEmitter } from '@decaf-ts/for-angular';
import { IonApp, IonContent, IonMenu, IonRouterOutlet, IonSplitPane } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  debounce,
  filter,
  fromEvent,
  map,
  merge,
  shareReplay,
  takeUntil,
  timer,
} from 'rxjs';
import { AppName } from './app.config';
import { AppMenuComponent } from './components/menu/menu.component';
import { NgxEwBasePage } from './utils/NgxEwBasePage';

/**
 * @description Root component of the Decaf-ts for Angular application
 * @summary This component serves as the main entry point for the application.
 * It sets up the navigation menu, handles routing events, and initializes
 * the application state. It also manages the application title and menu visibility.
 * @class
 * @param {Platform} platform - Ionic Platform service
 * @param {Router} router - Angular Router service
 * @param {MenuController} menuController - Ionic MenuController service
 * @param {Title} titleService - Angular Title service
 * @example
 * <app-root></app-root>
 * @mermaid
 * sequenceDiagram
 *   participant App as AppComponent
 *   participant Router
 *   participant MenuController
 *   participant TitleService
 *   participant Repository
 *   App->>App: constructor()
 *   App->>App: ngOnInit()
 *   App->>Router: Subscribe to events
 *   Router-->>App: Navigation events
 *   App->>MenuController: Enable/Disable menu
 *   App->>TitleService: Set page title
 *   App->>App: initializeApp()
 *   alt isDevelopmentMode
 *     App->>Repository: Initialize repositories
 *   end
 */
@Component({
  standalone: true,
  selector: 'app-root',
  imports: [
    IonApp,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonRouterOutlet,
    TranslateModule,
    IconComponent,
    AppMenuComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  schemas: [],
  providers: [],
})
export class AppComponent extends NgxEwBasePage implements OnInit {
  menuCollapsed: boolean = true;

  menuFixed: boolean = false;

  showCollapseButton: boolean = true;

  appDescription: string = 'Product Trust Platform';

  loggedIn: boolean = false;

  windowWidth: number = window.innerWidth;

  private awaiter$ = new BehaviorSubject<number>(0);

  constructor() {
    super('');
    this.appName = AppName;
  }

  /**
   * @description Lifecycle hook that is called after data-bound properties of a directive are initialized
   * @summary Sets up router event subscriptions and initializes the application
   * @return {Promise<void>}
   */
  async ngOnInit(): Promise<void> {
    this.hasMenu.set(true);
    this.title = 'PTP Frontend';
    this.appName = AppName;
    await this.initialize();
  }

  /**
   * @description Initializes the application
   * @summary Sets the initialized flag and sets up repositories if in development mode
   * @return {Promise<void>}
   */
  override async initialize(): Promise<void> {
    if (!this.showCollapseButton) {
      this.menuCollapsed = false;
    }
    this.router.events
      .pipe(
        takeUntil(this.destroySubscriptions$),
        filter((event) => event instanceof NavigationStart)
      )
      .subscribe((event: NavigationStart) => {
        const { url } = event || '';

        if (['login', ''].includes(url.replace('/', ''))) {
          this.awaiter$.next(6000);
        } else {
          this.awaiter$.next(0);
        }
      });

    // Await for login subscription to emit before setting loggedIn and hasMenu to ensure the correct state is set after login/logout
    combineLatest([this.authService.session$, this.awaiter$])
      .pipe(takeUntil(this.destroySubscriptions$), shareReplay({ bufferSize: 1, refCount: true }))
      .subscribe(([logged, awaiter]) => {
        timer(awaiter).subscribe(() => {
          this.loggedIn = logged;
          this.hasMenu.set(logged);
          if (logged) {
            this.handleDebounceHover();
          }
          this.changeDetectorRef.detectChanges();
        });
      });
    await super.initialize();

    // merge(fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange'))
    //   .pipe(
    //     filter(() => document.visibilityState === 'visible'),
    //     takeUntil(this.destroySubscriptions$)
    //   )
    //   .subscribe(() => {
    //     if (this.windowWidth >= 1200 && !this.menuFixed && !this.menuCollapsed) {
    //       this.menuCollapsed = true;
    //       this.changeDetectorRef.detectChanges();
    //     }
    //   });

    if (this.showCollapseButton) {
      timer(100)
        .pipe(takeUntil(this.destroySubscriptions$), shareReplay({ bufferSize: 1, refCount: true }))
        .subscribe(() => {
          windowEventEmitter('SidebarToggle', { collapsed: this.menuCollapsed });
        });
      this.mediaService
        .windowResizeObserver()
        .pipe(takeUntil(this.destroySubscriptions$), shareReplay({ bufferSize: 1, refCount: true }))
        .subscribe((size: IWindowResizeEvent) => {
          //TODO: review this, it is not working as expected, the menu is not collapsing when the window is resized
          // this.windowWidth = size.width;
          // if (size.width < 1200) {
          //   if (this.menuCollapsed) {
          //     this.menuCollapsed = this.menuFixed = false;
          //     this.changeDetectorRef.detectChanges();
          //   }
          // } else {
          //   if (!this.menuFixed) {
          //     this.menuCollapsed = true;
          //   }
          // }
        });
    }

    this.initialized = true;
  }

  private handleDebounceHover(): void {
    const element = this.component?.nativeElement;
    if (element) {
      const toggleState$ = (eventName: string = 'mouseenter') =>
        fromEvent<MouseEvent>(element, eventName).pipe(
          map((event) => ({
            action: eventName === 'mouseenter' ? 'open' : 'close',
            target: (event.relatedTarget as HTMLElement) ?? undefined,
          }))
        );

      merge(toggleState$(), toggleState$('mouseleave'))
        .pipe(
          debounce(({ action }) => (action === 'open' ? timer(200) : timer(1000))),
          takeUntil(this.destroySubscriptions$)
        )
        .subscribe(({ action, target }) => {
          if (this.windowWidth < 1200) {
            this.menuCollapsed = this.menuFixed = false;
          } else {
            if (!this.menuFixed) {
              this.menuCollapsed = action === 'close' && target !== undefined;
            }
          }
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  handleCollapseMenu(state?: string) {
    if (this.windowWidth >= 1200) {
      if (!state) {
        if (!this.menuCollapsed) {
          this.menuFixed = !this.menuFixed;
          if (!this.menuFixed) {
            this.menuCollapsed = true;
          }
        } else {
          this.menuCollapsed = !this.menuCollapsed;
          this.menuFixed = false;
        }
      } else {
        if (!this.menuFixed) {
          this.menuCollapsed = true;
        }
      }
      windowEventEmitter('SidebarToggle', { collapsed: this.menuCollapsed });
      this.changeDetectorRef.detectChanges();
    }
  }
}
